import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API_BASE } from "@/lib/api";
import { serverMessage } from "./ReferCopy";

/** The helpdesk — for the people the gate cannot let through: their number on record is wrong, or
 *  there is none. They pick themselves, leave a number that does reach them and say what went
 *  wrong; it lands in the HR Panel under Helpdesk. */
export const ReferHelpdesk = ({ t, open, onOpenChange, me }) => {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState(null);
  const [who, setWho] = useState(null);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null); setDone(false);
    if (me) { setWho({ emp_id: me.emp_id, name: me.name }); setQ(""); setMatches(null); }
  }, [open, me]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 3 || who) { setMatches(null); return undefined; }
    const timer = setTimeout(() => {
      axios.get(`${API_BASE}/public/refer/search`, { params: { q: term } })
        .then(({ data }) => setMatches(data.matches || []))
        .catch(() => setMatches([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [q, who]);

  const send = async () => {
    setBusy(true); setError(null);
    try {
      await axios.post(`${API_BASE}/public/refer/helpdesk`, {
        emp_id: who.emp_id, phone, message,
      });
      setDone(true);
    } catch (e) {
      setError(serverMessage(e?.response?.data?.detail, t));
    } finally { setBusy(false); }
  };

  const reset = () => {
    setWho(null); setQ(""); setMatches(null); setPhone(""); setMessage(""); setDone(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto"
        data-testid="refer-helpdesk-dialog">
        <DialogHeader>
          <DialogTitle>{t.helpTitle}</DialogTitle>
          <DialogDescription className="text-[13px] leading-relaxed">{t.helpLead}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-4 text-center" data-testid="refer-helpdesk-done">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <p className="mt-2 text-[14px]">{t.helpDone}</p>
            <Button className="mt-4 h-11 w-full" onClick={() => onOpenChange(false)}
              data-testid="refer-helpdesk-close">{t.close}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="mb-1.5 text-[13px] font-semibold">{t.helpWho}</p>
              {who ? (
                <div className="flex items-center justify-between gap-2 rounded-xl border bg-muted/40 px-3 py-2.5">
                  <span className="truncate text-[14px] font-medium" data-testid="refer-helpdesk-who">
                    {who.emp_id} · {who.name}
                  </span>
                  <Button variant="ghost" size="sm" className="shrink-0 text-[12px]"
                    onClick={() => setWho(null)} data-testid="refer-helpdesk-change">
                    {t.notYou}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)}
                      placeholder={t.searchPlaceholder} className="h-11 pl-9"
                      data-testid="refer-helpdesk-search" />
                  </div>
                  <div className="mt-1.5 max-h-40 space-y-1.5 overflow-y-auto">
                    {(matches || []).map((m) => (
                      <button key={m.emp_id} type="button"
                        onClick={() => setWho({ emp_id: m.emp_id, name: m.name })}
                        data-testid={`refer-helpdesk-match-${m.emp_id}`}
                        className="w-full rounded-lg border px-3 py-2 text-left text-[13px] hover:bg-muted/50">
                        {m.emp_id} · {m.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div>
              <p className="mb-1.5 text-[13px] font-semibold">{t.helpNumber}</p>
              <Input value={phone} inputMode="numeric" maxLength={10}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder={t.phoneHint} className="h-11 text-[15px]"
                data-testid="refer-helpdesk-phone" />
            </div>

            <div>
              <p className="mb-1.5 text-[13px] font-semibold">{t.helpMessage}</p>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={t.helpPlaceholder} rows={3} maxLength={500}
                data-testid="refer-helpdesk-message" />
            </div>

            {error && (
              <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive"
                data-testid="refer-helpdesk-error">{error}</p>
            )}

            <Button
              className="h-12 w-full bg-emerald-600 text-[15px] font-semibold text-white hover:bg-emerald-700"
              disabled={busy || !who || phone.length !== 10 || message.trim().length < 5}
              onClick={send} data-testid="refer-helpdesk-send"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t.helpSend}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
